/**
 * ShieldPanel — "Manage Shield Generator" (mechanical yard). The ship model sits
 * on the right with a central blue slot for the equipped generator; the left
 * panel lists every shield generator the player owns, with its rarity + stats.
 * Tap a generator to equip it; tap the slot to remove it.
 */
import type { HudSnapshot } from "@/engine/GameState";
import { RARITY_META } from "@/game/mods/modifications";

const BASE = import.meta.env.BASE_URL;

interface Props {
  snap: HudSnapshot;
  onEquip: (id: string) => void;
  onUnequip: () => void;
  onUpgrade: (id: string) => void;
  onClose: () => void;
}

export default function ShieldPanel({ snap, onEquip, onUnequip, onUpgrade, onClose }: Props) {
  const shields = snap.mods.filter((m) => m.kind === "shield");
  const equipped = shields.find((m) => m.id === snap.equippedShieldModId) ?? null;
  const shipUrl =
    snap.arsenal.hullTier >= 5
      ? snap.arsenal.mkvPath === "swarmlord"
        ? `${BASE}sprites/${snap.arsenal.isMkviiSwl ? "MTS_VII_SWL" : "MTS_VI_SWL"}.png`
        : `${BASE}sprites/MTS_VI_BTS.png`
      : snap.arsenal.hullTier >= 4
        ? `${BASE}sprites/${snap.arsenal.mkvPath === "swarmlord" ? "MTS_V_SWL" : "MTS_V_BTS"}.png`
        : `${BASE}sprites/MTS_${Math.min(4, snap.arsenal.hullTier + 1)}.png`;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel shield-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Shield Generator"
      >
        <div className="upg-head">
          <div className="upg-head-title">🛡 Shield Generator</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="shield-body">
          <div className="shield-list">
            <div className="shield-list-title">Your generators</div>
            {shields.length > 0 && (
              <div className="shield-list-hint">
                Tap a generator to equip · <b>▲</b> upgrades it (◆ Units)
              </div>
            )}
            {shields.length === 0 && (
              <div className="inv-empty">
                No generators yet — build one at a 🛒 trade hub (Build Mods).
              </div>
            )}
            {shields.map((m) => {
              const col = RARITY_META[m.rarity].color;
              const on = equipped?.id === m.id;
              const upCost = snap.modUpgradeCosts[m.id] ?? -1;
              const maxed = upCost < 0;
              const canUp = !maxed && snap.units >= upCost;
              return (
                <div
                  key={m.id}
                  className={`shield-card${on ? " equipped" : ""}`}
                  style={{ borderColor: col }}
                >
                  <button
                    className="shield-card-main"
                    onClick={() => (on ? onUnequip() : onEquip(m.id))}
                  >
                    <span className="shield-card-rarity" style={{ color: col }}>
                      {RARITY_META[m.rarity].label}
                      <span className="shield-card-lv">Lv {m.level ?? 1}</span>
                    </span>
                    <span className="shield-card-stat">+{m.shieldBonus} shield</span>
                    <span className="shield-card-state">{on ? "Equipped ✓" : "Equip"}</span>
                  </button>
                  <button
                    className="shield-card-up"
                    disabled={!canUp}
                    onClick={() => onUpgrade(m.id)}
                    title={
                      maxed
                        ? "Max level reached"
                        : `Upgrade to Lv ${(m.level ?? 1) + 1} — ◆ ${upCost} U`
                    }
                  >
                    {maxed ? "MAX" : `▲ ◆ ${upCost}`}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="shield-diagram">
            <img className="arsenal-ship" src={shipUrl} alt="Carrier hull" />
            <button
              className={`shield-slot${equipped ? " filled" : ""}`}
              style={
                equipped
                  ? {
                      borderColor: RARITY_META[equipped.rarity].color,
                      boxShadow: `0 0 12px ${RARITY_META[equipped.rarity].color}`,
                    }
                  : undefined
              }
              onClick={equipped ? onUnequip : undefined}
              title={equipped ? "Tap to remove" : "Equip a generator from the left"}
            >
              {equipped ? (
                <span className="shield-slot-val">
                  🛡<b>+{equipped.shieldBonus}</b>
                </span>
              ) : (
                "＋"
              )}
            </button>
            <div className="shield-total">
              Max shield: <b>{Math.round(snap.maxShield)}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
